export interface AuthUser {
  name: string;
  email: string;
}

type UserPayload = Record<string, any>;

export const AUTH_USER_STORAGE_KEY = "authUser";

const getStringValue = (...values: unknown[]) =>
  values.find((value) => typeof value === "string" && value.trim().length > 0) as
    | string
    | undefined;

const getEmailValue = (...values: unknown[]) =>
  getStringValue(...values.filter((value) => typeof value === "string" && value.includes("@")));

const getUserPayload = (payload: UserPayload): UserPayload => {
  return (
    payload.user ||
    payload.admin ||
    payload.data?.user ||
    payload.data?.admin ||
    payload
  );
};

export const getAuthUserFromPayload = (payload: unknown): AuthUser | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const user = getUserPayload(payload as UserPayload);
  const firstName = getStringValue(user.firstName, user.firstname, user.given_name);
  const lastName = getStringValue(user.lastName, user.lastname, user.family_name);
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const name = getStringValue(user.name, user.fullName, user.displayName, fullName);
  const email = getEmailValue(
    user.email,
    user.emailAddress,
    user.adminEmail,
    user.preferred_username,
    user.sub
  );

  if (!name && !email) {
    return null;
  }

  return {
    name: name || email || "Admin",
    email: email || "",
  };
};

export const getAuthUserInitial = (user: AuthUser | null) => {
  return (user?.name || user?.email || "A").charAt(0).toUpperCase();
};

export const saveAuthUser = (payload: unknown) => {
  const user = getAuthUserFromPayload(payload);

  if (user) {
    localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
  }
};

const parseJwtPayload = (token: string): UserPayload | null => {
  try {
    const base64Url = token.split(".")[1];

    if (!base64Url) {
      return null;
    }

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const getStoredAuthUser = (): AuthUser => {
  const storedUser = localStorage.getItem(AUTH_USER_STORAGE_KEY);

  if (storedUser) {
    try {
      const user = getAuthUserFromPayload(JSON.parse(storedUser));

      if (user) {
        return user;
      }
    } catch {
      localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    }
  }

  const token = localStorage.getItem("authToken");
  const tokenUser = token ? getAuthUserFromPayload(parseJwtPayload(token)) : null;

  return tokenUser || { name: "Admin", email: "" };
};

export const clearAuthUser = () => {
  localStorage.removeItem(AUTH_USER_STORAGE_KEY);
};
